import { Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@lib/cn';
import { useNavCollapsed } from '@state/useNavCollapsed';
import { useCurrentProgramSlug, useCurrentCampaign } from '@state/useCurrentProgramSlug';
import { useSetupCampaigns } from '@state/useSetupCampaigns';
import { buildNavGroups } from './navStructure';

/**
 * Demo-only extra: an external call-to-action below the nav. Rendered ONLY when
 * the global `demo_mode` setting is true (same gate as DemoDataBanner), so it is
 * inert/absent for any production tenant (e.g. Alignment never sets demo_mode).
 *
 * The label reads "Meet Zara" and must keep saying Zara (William 2026-08-05):
 * the destination page is written around Zara, and the live voice agent opens
 * every call with "it's Zara calling". A link labelled with any other name
 * contradicts the first sentence the visitor hears.
 */
const MEET_ZARA_URL = 'https://cdxdemomedia19139.z5.web.core.windows.net/';

/**
 * Demo-only extra #2: internal route to the ClariLens AI analytics chat.
 * Same demoMode gate as Meet Zara, so it never renders for production
 * tenants; the /api/ask endpoint additionally hard-refuses non-demo tenants.
 */
const CLARILENS_PATH = '/clarilens';

/**
 * Nav — editorial sidebar (paper-2 bg, mono-numbered items, teal active-border).
 *
 * 4-group / 11-item structure (William nav redesign 2026-05-22). The group +
 * item definitions — including the quick-key letters — live in navStructure.ts,
 * shared with useKeyboardShortcuts so every displayed key matches its chord.
 *
 * Every item shows its quick key in the right-hand .meta column; PHI-tier
 * items show the tier badge alongside the key.
 *
 * Collapse pattern (mirrors SlicersRail):
 *   - Expanded 260px: full numbered + labeled item list grouped by section
 *   - Collapsed 56px: number-only mini-strip (clicking expands)
 *   - Toggle in top-right (◀ collapse / ▶ expand), state persisted to localStorage
 */

export function Nav() {
  const location = useLocation();
  const { collapsed, toggle } = useNavCollapsed();
  // Was `program ? /${program} : '/fobt'` — meant clicking a no-program route
  // (e.g. /setup) and then any left-nav link sent you to /fobt/*, ignoring
  // the chip strip's selected campaign. Now derives from URL → store →
  // first-active so the nav follows the user's actual program.
  // (William 2026-05-25 cross-campaign segregation.)
  const programPrefix = `/${useCurrentProgramSlug()}`;
  // Per-campaign page visibility: a fulfillment-only campaign hides pages with
  // no data (e.g. Clinical / Sentiment / Predictive for BP Cuffs).
  const hiddenKeys = useCurrentCampaign()?.hiddenPages ?? [];
  // Demo-only "Meet Zara" CTA gate (inert for production tenants).
  const demoMode = useSetupCampaigns((s) => s.global)?.demoMode ?? false;

  const groups = buildNavGroups(programPrefix, hiddenKeys);

  // Flat strip for collapsed view — numbered items with separators between groups.
  const stripItems = groups.flatMap((g, gi) =>
    g.items.map((it, ii) => ({ ...it, groupIndex: gi, itemIndex: ii })),
  );

  return (
    <nav
      className={cn(collapsed && 'collapsed')}
      aria-label="primary navigation"
    >
      <button
        type="button"
        className="nav-toggle"
        onClick={toggle}
        title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      >
        {collapsed ? '▶' : '◀'}
      </button>

      {/* Expanded: full grouped list */}
      <div className="nav-body side-pad">
        {groups.map((g) => (
          <div key={g.label} className="side-group">
            <div className="side-label">{g.label}</div>
            {g.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => cn('side-item', isActive && 'active')}
                style={{ width: '100%', textAlign: 'left', textDecoration: 'none' }}
              >
                <span className="num">{item.num}</span>
                <span className="name">{item.label}</span>
                <span
                  className="meta"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {item.tier && (
                    <span style={{ color: item.tier === 3 ? 'var(--crimson)' : 'var(--amber)' }}>
                      PHI-{item.tier}
                    </span>
                  )}
                  <span>g {item.shortcut.toLowerCase()}</span>
                </span>
              </NavLink>
            ))}
          </div>
        ))}

        {/* Demo-only "Meet Zara" CTA — below ADDITIONAL PROGRAMS, external link. */}
        {demoMode && (
          <div className="side-group" style={{ marginTop: 4 }}>
            <a
              href={MEET_ZARA_URL}
              target="_blank"
              rel="noopener noreferrer"
              title="Meet Zara"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                margin: '2px 0',
                padding: '10px 12px',
                background: '#0E726E',
                color: '#fff',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: '0.02em',
                textDecoration: 'none',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.12)',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>✦</span>
              Meet Zara
            </a>
            {/* Demo-only "Ask ClariLens" CTA — internal route to the AI chat page. */}
            <NavLink
              to={CLARILENS_PATH}
              title="Ask ClariLens"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                margin: '6px 0 2px',
                padding: '10px 12px',
                background: '#0E726E',
                color: '#fff',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: '0.02em',
                textDecoration: 'none',
                boxShadow:
                  location.pathname === CLARILENS_PATH
                    ? 'inset 0 0 0 2px rgba(255, 255, 255, 0.65), 0 1px 2px rgba(0, 0, 0, 0.12)'
                    : '0 1px 2px rgba(0, 0, 0, 0.12)',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>✦</span>
              Ask ClariLens
            </NavLink>
          </div>
        )}
      </div>

      {/* Collapsed: number-only mini-strip with group separators */}
      <div className="nav-strip">
        {stripItems.map((it, i) => {
          const isActive = location.pathname === it.path || location.pathname.startsWith(it.path + '/');
          const prev = i > 0 ? stripItems[i - 1] : undefined;
          const showSep = prev !== undefined && it.groupIndex !== prev.groupIndex;
          return (
            <Fragment key={it.path}>
              {showSep && <div className="nav-strip-sep" />}
              <NavLink
                to={it.path}
                className={cn('nav-strip-item', isActive && 'active')}
                title={`${it.num} · ${it.label} (g ${it.shortcut.toLowerCase()})`}
                style={{ textDecoration: 'none' }}
              >
                <span className="num">{it.num}</span>
              </NavLink>
            </Fragment>
          );
        })}
        {demoMode && (
          <>
            <div className="nav-strip-sep" />
            <a
              href={MEET_ZARA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-strip-item"
              title="Meet Zara"
              style={{ textDecoration: 'none', color: '#0E726E' }}
            >
              <span className="num" aria-hidden="true">✦</span>
            </a>
            <NavLink
              to={CLARILENS_PATH}
              className={cn('nav-strip-item', location.pathname === CLARILENS_PATH && 'active')}
              title="Ask ClariLens"
              style={{ textDecoration: 'none', color: '#0E726E' }}
            >
              <span className="num" aria-hidden="true">✦</span>
            </NavLink>
          </>
        )}
      </div>
    </nav>
  );
}
